import React from "react";

import sortBy from "lodash/sortBy";

import Loading from "components/Loading";
import Panel from "components/Panel";
import QueryStatsTable, {
  QueryStatsTableSortBy,
} from "components/QueryStatsTable";

import QueryDataMissingHint from "components/QueryPerformance/QueryList/QueryDataMissingHint";
import { formatNumber } from "utils/format";

import {
  SchemaTableQueriesQuery,
  useSchemaTableQueriesQuery,
} from "./gql/Query.generated";
import { useSchemaTableQueriesScansQuery } from "./gql/Query.scans.generated";
import { useDateRange } from "components/WithDateRange";
import { useFeature } from "components/OrganizationFeatures";
import Grid from "components/Grid";
import SchemaTableScanDetails from "components/SchemaTableScanDetails";
import { useQueryStringSearch } from "components/QueryStringSearch";
import { useQueryStringSort } from "utils/hooks";

interface Props {
  databaseId: string;
  tableId: string;
  indexId?: string;
}

const SchemaTableQueries: React.FunctionComponent<Props> = (props) => {
  const [searchTerm, SearchInput] = useQueryStringSearch("q");
  const indexAdvisor = useFeature("indexAdvisor");

  return (
    <>
      {indexAdvisor && (
        <Panel title={"Scans on " + (props.indexId ? "Index" : "Table")}>
          <SchemaTableScansContent {...props} />
        </Panel>
      )}
      {!props.indexId && (
        <Panel title="Queries" secondaryTitle={<SearchInput />}>
          <SchemaTableQueriesContent {...props} searchTerm={searchTerm} />
        </Panel>
      )}
    </>
  );
};

const FETCH_BATCH_SIZE = 100;

const SchemaTableQueriesContent: React.FunctionComponent<
  Props & { searchTerm: string }
> = ({ databaseId, tableId, searchTerm }) => {
  const { from, to } = useDateRange();
  const [sortOpts, setSortOpts] = useQueryStringSort<QueryStatsTableSortBy>({
    param: "sort",
    sortBy: "pctOfTotal",
    sortDirection: "DESC",
  });

  const { data, loading, error, fetchMore } = useSchemaTableQueriesQuery({
    variables: {
      databaseId,
      tableId,
      startTs: from.unix(),
      endTs: to.unix(),
      offset: 0,
      limit: FETCH_BATCH_SIZE,
      filter: searchTerm,
      sortBy: sortOpts.sortBy,
      sortDirection: sortOpts.sortDirection,
    },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  if (data.getQueryStatsForTable.length === 0) {
    return <QueryDataMissingHint databaseId={databaseId} />;
  }

  const fetchMoreData = (loadedCount: number) => {
    return new Promise<boolean>((resolve) => {
      const offset = loadedCount;
      fetchMore({
        variables: { offset, limit: FETCH_BATCH_SIZE },
        updateQuery: (
          prev: SchemaTableQueriesQuery,
          { fetchMoreResult }: { fetchMoreResult: SchemaTableQueriesQuery },
        ): SchemaTableQueriesQuery => {
          const fetched = fetchMoreResult.getQueryStatsForTable;
          resolve(fetched.length > 0);
          if (!prev || !prev.getQueryStatsForTable) {
            return fetchMoreResult;
          }
          return {
            ...prev,
            getQueryStatsForTable: [...prev.getQueryStatsForTable, ...fetched],
          };
        },
      });
    });
  };

  return (
    <QueryStatsTable
      databaseId={databaseId}
      serverId={data.getServerDetails.id}
      queries={data.getQueryStatsForTable}
      fetchMoreData={fetchMoreData}
      sortOpts={sortOpts}
      setSortOpts={setSortOpts}
      searchTerm={null}
      compare={false}
      showingTable
    />
  );
};

const SchemaTableScansContent: React.FunctionComponent<Props> = ({
  databaseId,
  tableId,
  indexId,
}) => {
  const { from, to } = useDateRange();

  const { data, loading, error } = useSchemaTableQueriesScansQuery({
    variables: {
      databaseId,
      tableId,
      indexId,
      startTs: from.unix(),
      endTs: to.unix(),
    },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  if (data.getSchemaTableScans.length === 0) {
    return <QueryDataMissingHint databaseId={databaseId} />;
  }

  const scanData = data.getSchemaTableScans.map((scan) => {
    const combinedSql =
      scan.whereExpression && scan.joinExpression
        ? `(${scan.whereExpression}) AND (${scan.joinExpression})`
        : scan.whereExpression || scan.joinExpression || "";

    return {
      combinedSql: combinedSql,
      whereExpression: scan.whereExpression,
      joinExpression: scan.joinExpression,
      scanMethod: scan.genericPlanScanMethod,
      totalCost: scan.genericPlanTotalCost,
      scansPerMin: scan.avgCallsPerMinute,
      referencedQueries: scan.queries.map((q) => q.id),
    };
  });
  const sortedScanData = sortBy(scanData, [(scan) => -scan.scansPerMin]);

  return (
    <Grid
      data={sortedScanData}
      columns={[
        {
          field: "combinedSql",
          header: "Scan Expression",
          width: "minmax(0,5fr)",
          tip: "Combined conditions on data fetched during this scan.",
          renderer: function ScanDetailsCell({ rowData }) {
            return (
              <SchemaTableScanDetails
                databaseId={databaseId}
                whereExpression={rowData.whereExpression}
                joinExpression={rowData.joinExpression}
                referencedQueries={rowData.referencedQueries}
              />
            );
          },
        },
        {
          field: "scanMethod",
          header: "Scan Method",
          width: "minmax(0,2fr)",
          title: true,
          nullValue: "Unknown",
        },
        {
          field: "totalCost",
          header: "Cost",
          width: "minmax(0,1fr)",
          style: "number",
          nullValue: "?",
          renderer: ({ fieldData }) => formatNumber(fieldData, 2),
        },
        {
          field: "scansPerMin",
          header: "Est. Scans / Min",
          width: "minmax(0,1fr)",
          style: "number",
          renderer: ({ fieldData }) => formatNumber(fieldData, 4),
        },
      ]}
    />
  );
};

export default SchemaTableQueries;
